import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useSyncExternalStore } from "react"
import { fetchAuthSession, signOut as amplifySignOut } from "aws-amplify/auth"
import { Hub } from "aws-amplify/utils"

// The app's view of the Cognito session. Components read a synchronous
// accessToken through useSession() (the API client takes the raw token per
// call), App.tsx gates on isLoading/isAuthenticated, and sign-out goes
// through signOut().
//
// The state itself lives in a module-level store (not React state) so the
// API client can ask for a fresh token or report a rejected one without a
// React dependency; SessionProvider is a thin useSyncExternalStore wrapper.
//
// Amplify refreshes tokens on its own inside fetchAuthSession (access token
// 60 min, refresh token 30 days, no rotation). The store re-reads the
// session shortly before the access token expires, when the tab wakes up
// (visible / focus / online) and on Amplify's Hub events. A refresh that
// fails TRANSIENTLY (network down after laptop wake, Cognito 5xx, throttling,
// a hung request) keeps the current token and retries with backoff — only a
// DEFINITIVE failure (refresh token expired/revoked, user gone) or a missing
// session ends it, which App.tsx answers with the login redirect.

export interface Session {
  accessToken: string | undefined
  isLoading: boolean
  isAuthenticated: boolean
  // the last session read/refresh failed transiently and a retry is pending
  degraded: boolean
  // retry the pending read/refresh right now (App's "Reconnecting" screen)
  retry: () => void
  // global: also GlobalSignOut (every device's refresh tokens) — used after
  // MFA enrollment; plain: revoke this refresh token + clear the shared cookies
  signOut: (options?: { global?: boolean }) => Promise<void>
}

const SessionContext = createContext<Session | undefined>(undefined)

// ---------------------------------------------------------------------------
// store

type SignedOutReason = "noTokens" | "definitive" | "hubSignedOut" | "explicit"

type SessionState =
  | { kind: "loading"; attempts: number }
  | { kind: "authenticated"; accessToken: string; expiresAt: number; failures: number }
  | { kind: "signedOut"; reason: SignedOutReason }   // terminal for this page

// Amplify clears the stored tokens for exactly these (TokenOrchestrator.
// isAuthenticationError); everything else it throws is worth retrying.
const DEFINITIVE_ERRORS = [
  "NotAuthorizedException",
  "TokenRevokedException",
  "UserNotFoundException",
  "PasswordResetRequiredException",
  "UserNotConfirmedException",
  "RefreshTokenReuseException",
]

// refresh this long before the access token expires (60 min tokens)
const REFRESH_MARGIN_MS = 2 * 60 * 1000
// a cached token with at least this much life left is handed out as is
const FRESH_MIN_MS = 30 * 1000
// Amplify's own fetch has no timeout; a request that hangs after laptop wake
// must not wedge the store
const READ_TIMEOUT_MS = 15 * 1000
// transient-failure retry delays; the last one repeats
const RETRY_DELAYS_MS = [5 * 1000, 15 * 1000, 60 * 1000]

let state: SessionState = { kind: "loading", attempts: 0 }
const listeners = new Set<() => void>()
let inFlight: Promise<void> | undefined
let inFlightForce = false
let timer: ReturnType<typeof setTimeout> | undefined
let explicitSignOut = false
let started = false

function setState(next: SessionState) {
  state = next
  listeners.forEach((listener) => listener())
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

function getSnapshot(): SessionState {
  return state
}

// read through a call so TypeScript does not carry a stale narrowing of the
// module-level `state` across the awaits in readSession
function isSignedOut(): boolean {
  return getSnapshot().kind === "signedOut"
}

function errorName(err: unknown): string {
  const name = (err as { name?: unknown } | null | undefined)?.name
  return typeof name === "string" ? name : "Error"
}

function isDefinitive(err: unknown): boolean {
  const name = errorName(err)
  return DEFINITIVE_ERRORS.some((definitive) => name.startsWith(definitive))
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const handle = setTimeout(() => {
      const err = new Error(`session read timed out after ${ms} ms`)
      err.name = "TimeoutError"
      reject(err)
    }, ms)
    promise.then(
      (value) => { clearTimeout(handle); resolve(value) },
      (err) => { clearTimeout(handle); reject(err) },
    )
  })
}

function clearTimer() {
  if (timer !== undefined) {
    clearTimeout(timer)
    timer = undefined
  }
}

// one timer slot: either the proactive refresh or the transient-failure retry
function armTimer(delay: number, force: boolean) {
  clearTimer()
  timer = setTimeout(() => {
    timer = undefined
    void readSession(force)
  }, delay)
}

function signedOut(reason: SignedOutReason) {
  if (state.kind === "signedOut")
    return
  clearTimer()
  console.info(`session: signed out (${reason})`)
  setState({ kind: "signedOut", reason })
}

// The single loader; concurrent calls share one read. A forced read asked
// for while a plain one is in flight runs after it, unless that one already
// produced a fresh token.
function readSession(force: boolean): Promise<void> {
  if (state.kind === "signedOut")
    return Promise.resolve()
  if (inFlight) {
    if (force && !inFlightForce)
      return inFlight.then(() => hasFreshToken(REFRESH_MARGIN_MS) ? undefined : readSession(true))
    return inFlight
  }
  inFlightForce = force
  inFlight = (async () => {
    try {
      const { tokens } = await withTimeout(fetchAuthSession({ forceRefresh: force }), READ_TIMEOUT_MS)
      if (isSignedOut())
        return
      const token = tokens?.accessToken
      const exp = token?.payload.exp
      if (token === undefined || typeof exp !== "number") {
        // no cookies (never signed in, or another app signed out), or Amplify
        // cleared them after a NotAuthorizedException on refresh
        signedOut("noTokens")
        return
      }
      const expiresAt = exp * 1000
      const changed = state.kind !== "authenticated" || state.accessToken !== token.toString()
      setState({ kind: "authenticated", accessToken: token.toString(), expiresAt, failures: 0 })
      const delay = Math.max(5 * 1000, expiresAt - REFRESH_MARGIN_MS - Date.now())
      armTimer(delay, true)
      if (changed || force)
        console.info(`session: token ${changed ? "updated" : "unchanged"}, expires ${new Date(expiresAt).toLocaleTimeString()}, next refresh in ${Math.round(delay / 1000)} s`)
    } catch (err) {
      if (isSignedOut())
        return
      if (isDefinitive(err)) {
        console.warn("session: refresh rejected:", err)
        signedOut("definitive")
        return
      }
      const failures = state.kind === "loading" ? state.attempts : state.failures
      const delay = RETRY_DELAYS_MS[Math.min(failures, RETRY_DELAYS_MS.length - 1)]
      console.warn(`session: transient ${force ? "refresh" : "read"} failure (${errorName(err)}), retry in ${delay / 1000} s`, err)
      setState(state.kind === "loading"
        ? { kind: "loading", attempts: failures + 1 }
        : { ...state, failures: failures + 1 })
      armTimer(delay, force)
    } finally {
      inFlight = undefined
      inFlightForce = false
    }
  })()
  return inFlight
}

function hasFreshToken(minLifeMs: number): boolean {
  return state.kind === "authenticated" && state.expiresAt - Date.now() > minLifeMs
}

// tab woke up (visible / focus / online): re-read cheaply — picks up a token
// refreshed by the login or buy app and notices cookies wiped by them — or
// refresh when the token is about to expire or a retry is pending
function onWake() {
  if (document.visibilityState !== "visible" || state.kind === "signedOut")
    return
  if (state.kind === "authenticated" && (state.failures > 0 || !hasFreshToken(REFRESH_MARGIN_MS)))
    void readSession(true)
  else
    void readSession(false)
}

function start() {
  if (started)
    return
  started = true
  void readSession(false)

  document.addEventListener("visibilitychange", onWake)
  window.addEventListener("focus", onWake)
  window.addEventListener("online", onWake)

  Hub.listen("auth", ({ payload }) => {
    switch (payload.event) {
      case "tokenRefresh":
        // Amplify refreshed inside some other fetchAuthSession call
        void readSession(false)
        break
      case "signedOut":
        signedOut("hubSignedOut")
        break
      case "tokenRefresh_failure": {
        // fires for every refresh error; only the definitive ones cleared the
        // tokens (transient ones are retried by readSession's own catch)
        const error = (payload as { data?: { error?: unknown } }).data?.error
        if (isDefinitive(error))
          signedOut("definitive")
        break
      }
    }
  })
}

// ---------------------------------------------------------------------------
// module-level API (api.ts, App.tsx, signOut.ts)

// The freshest access token: the cached one while it has life left,
// otherwise a (deduped, timeout-guarded) refresh first. Falls back to the
// stale token when the refresh failed transiently, undefined once signed out.
export async function getFreshAccessToken(): Promise<string | undefined> {
  if (hasFreshToken(FRESH_MIN_MS))
    return state.kind === "authenticated" ? state.accessToken : undefined
  if (state.kind === "signedOut")
    return undefined
  await readSession(state.kind === "authenticated")
  return state.kind === "authenticated" ? state.accessToken : undefined
}

// The API rejected `rejectedToken` with 401: hand back the already-rotated
// token if there is one, otherwise force a refresh. Undefined = signed out.
export async function recoverFromUnauthorized(rejectedToken: string): Promise<string | undefined> {
  if (state.kind === "authenticated" && state.accessToken !== rejectedToken)
    return state.accessToken
  if (state.kind === "signedOut")
    return undefined
  await readSession(true)
  return state.kind === "authenticated" ? state.accessToken : undefined
}

// A deliberate sign-out (useSignOut) navigates on its own; App's redirect
// gate must not race it.
export function markExplicitSignOut() {
  explicitSignOut = true
}

export function isExplicitSignOut(): boolean {
  return explicitSignOut
}

export function retryNow() {
  if (state.kind === "signedOut")
    return
  clearTimer()
  void readSession(state.kind === "authenticated")
}

// ---------------------------------------------------------------------------
// React

export function SessionProvider({ children }: { children: ReactNode }) {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot)

  useEffect(() => { start() }, [])

  const signOut = useCallback(async (options?: { global?: boolean }) => {
    markExplicitSignOut()
    try {
      await amplifySignOut(options?.global ? { global: true } : undefined)
    } finally {
      signedOut("explicit")
    }
  }, [])

  const retry = useCallback(() => { retryNow() }, [])

  const accessToken = snapshot.kind === "authenticated" ? snapshot.accessToken : undefined
  const isLoading = snapshot.kind === "loading"
  const degraded = snapshot.kind === "loading" ? snapshot.attempts > 0
    : snapshot.kind === "authenticated" ? snapshot.failures > 0
      : false

  const value = useMemo<Session>(() => ({
    accessToken,
    isLoading,
    isAuthenticated: accessToken !== undefined,
    degraded,
    retry,
    signOut,
  }), [accessToken, isLoading, degraded, retry, signOut])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): Session {
  const session = useContext(SessionContext)
  if (session === undefined)
    throw new Error("useSession must be used inside SessionProvider")
  return session
}
